import React, { useState, useEffect, useReducer, useRef, useCallback } from "react";
import {
  Container,
  Tabs,
  Tab,
  Button,
  Box,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import Navbar from "./navigation";
import MessagesList from "./MessagesList";
import ReplyDrawer from "./ReplyDrawer";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import Footer from "./Footer";
import PaginationControl from "./utils/PaginationControl";
import GlobalNotification from "./components/GlobalNotification";
import usePolling from "./hooks/useMessagePolling";

// How often to silently re-fetch the active tab's messages (ms).
// 15s is a reasonable default — fresh enough to feel "live" without hammering the API.
const POLL_INTERVAL_MS = 15000;

const initialState = {
  inbox: { messages: [], loading: false, loaded: false, page: 1, hasMore: false },
  sent: { messages: [], loading: false, loaded: false, page: 1, hasMore: false },
  archive: { messages: [], loading: false, loaded: false, page: 1, hasMore: false },
};

function messageReducer(state, action) {
  switch (action.type) {
    case "SET_MESSAGES":
      return {
        ...state,
        [action.payload.type]: {
          ...state[action.payload.type],
          messages: action.payload.messages,
          page: action.payload.page,
          hasMore: action.payload.hasMore,
          loaded: true,
        },
      };
    case "SET_LOADING":
      return {
        ...state,
        [action.payload.type]: {
          ...state[action.payload.type],
          loading: action.payload.loading,
        },
      };
    default:
      return state;
  }
}

const InboxPage = () => {
  const [state, dispatch] = useReducer(messageReducer, initialState);
  const [tabIndex, setTabIndex] = useState(0);
  const [replyOpen, setReplyOpen] = useState(false);
  const [messageToReply, setMessageToReply] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const activeTab = ["inbox", "sent", "archive"][tabIndex];
      const [userId, setUserId] = useState(null);
const userRole = localStorage.getItem("userRole");
const [sortOrder, setSortOrder] = useState("Latest");

  // --- Auto-refresh bookkeeping ---
  // Refs (not state) so updating them never triggers a re-render or re-runs an effect.
  const hasSelectionRef = useRef(false);     // true while the user has rows checked in MessagesList
  const activeTabRef = useRef(activeTab);    // lets the interval always read the *current* tab/page
  const currentPageRef = useRef(1);
  const sortOrderRef = useRef(sortOrder);

  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => { currentPageRef.current = state[activeTab].page; }, [state, activeTab]);
  useEffect(() => { sortOrderRef.current = sortOrder; }, [sortOrder]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

const fetchMessages = async (type, page = 1, limit = 10, sort = sortOrder, { silent = false } = {}) => {
  // "silent" fetches (background polling) skip the loading spinner so the list
  // doesn't flicker/reset scroll position every 15s — only the data swaps in.
  if (!silent) {
    dispatch({ type: "SET_LOADING", payload: { type, loading: true } });
  }
  try {
    const email = localStorage.getItem("email");

    const url = type === "archive"
      ? ENDPOINTS.getArchiveMessages({ email, page, limit, sortOrder: sort })
      : ENDPOINTS.getUserMessages({ type, email, page, limit, sortOrder: sort });

    const resp = await api.get(url);
    const threads = resp.data?.data?.items || [];
    const total = resp.data?.data?.totalItems ?? 0;
    setTotalCount(total);

    dispatch({
      type: "SET_MESSAGES",
      payload: { type, messages: threads, page, hasMore: threads.length === limit },
    });
  } catch (e) {
    console.error(`Error fetching ${type} messages`, e);
    if (!silent) {
      showSnackbar(`Failed to load ${type} messages.`, "error");
    }
    // Silent/background failures stay quiet — a dropped poll tick shouldn't
    // interrupt the user with a toast; it'll just try again next interval.
  } finally {
    if (!silent) {
      dispatch({ type: "SET_LOADING", payload: { type, loading: false } });
    }
  }
};

  useEffect(() => {
    fetchMessages("inbox", 1);
  }, []);
     useEffect(() => {
      const id = localStorage.getItem("userId");
      if (id) setUserId(id);
    }, []);

usePolling(
  () => {
    fetchMessages(
      activeTabRef.current,
      currentPageRef.current,
      pageSize,
      sortOrderRef.current,
      { silent: true }
    );
  },
  {
    interval: POLL_INTERVAL_MS,
    pauseRef: hasSelectionRef,
  }
);
  // Called by MessagesList whenever its internal selection changes
  const handleSelectionChange = useCallback((hasSelection) => {
    hasSelectionRef.current = hasSelection;
  }, []);

  const handleTabChange = (_, idx) => {
    setTabIndex(idx);
    setMessageToReply(null);
    const type = ["inbox", "sent", "archive"][idx];
    fetchMessages(type, 1);
  };


  // Fixed archive function
const handleArchive = async (ids) => {
  const messageIds = Array.isArray(ids) ? ids : [ids];
  try {
    await api.post(ENDPOINTS.archiveMessages(), {
      originalMsgIds: messageIds,
      archiveFrom: "Director",
      activeFlag: true,
    });

    showSnackbar("Messages archived successfully.");
    const currentTab = ["inbox", "sent", "archive"][tabIndex];
    await fetchMessages(currentTab, state[currentTab].page);
  } catch (e) {
    console.error('Archive API error:', e.response?.data || e);
    showSnackbar("Failed to archive messages.", "error");
  }
};

  // Fixed move to inbox function
const handleMoveToInbox = async (ids) => {
  const messageIds = Array.isArray(ids) ? ids : [ids];
  try {
    await api.post(ENDPOINTS.archiveMessages(), {
      originalMsgIds: messageIds,
      archiveFrom: "Director",
      activeFlag: false,
    });

    showSnackbar("Messages moved to inbox successfully.");
    await fetchMessages("archive", state.archive.page);
  } catch (e) {
    console.error('Move to inbox API error:', e.response?.data || e);
    showSnackbar("Failed to move messages to inbox.", "error");
  }
};

  // Fixed delete function
 const handleDelete = async (messageIds) => {
  const email = localStorage.getItem("email");
  const ids = Array.isArray(messageIds) ? messageIds : [messageIds];
  try {
    await api.post(ENDPOINTS.deleteMessages(), {
      originalMsgIds: ids,
      userId: email,
      activeFlag: true,
      permanentArchive: true,
    });

showSnackbar("Messages deleted successfully.", "error");
    await fetchMessages("archive", state.archive.page);
  } catch (e) {
    console.error('Delete API error:', e.response?.data || e);
    showSnackbar("Failed to delete messages.", "error");
  }
};

  return (
    <>
      <Navbar />
      <div className="background-prop">
        <div className="shadow" style={{ maxWidth: 1200, margin: "auto", padding: 20 }}>
           <GlobalNotification userId={userId} userRole={userRole} />
          <Container>
            <Snackbar
              open={snackbar.open}
              autoHideDuration={3000}
              onClose={() => setSnackbar(s => ({ ...s, open: false }))}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>

            <Typography sx={{ fontSize: 30, mt: 2, mb: 2, fontWeight: 600 }}>
              Inbox
            </Typography>

            <Tabs value={tabIndex} onChange={handleTabChange}>
              <Tab label="Inbox" />
              <Tab label="Sent" />
              <Tab label="Archived" />
            </Tabs>

            <MessagesList
              messages={
                tabIndex === 0
                  ? state.inbox.messages
                  : tabIndex === 1
                    ? state.sent.messages
                    : state.archive.messages
              }
              loading={
                tabIndex === 0
                  ? state.inbox.loading
                  : tabIndex === 1
                    ? state.sent.loading
                    : state.archive.loading
              }
              onReply={msg => {
                setMessageToReply(msg);
                setReplyOpen(true);
              }}
              onArchive={tabIndex !== 2 ? handleArchive : undefined}
              onInbox={tabIndex === 2 ? handleMoveToInbox : undefined}
              onDelete={tabIndex === 2 ? handleDelete : undefined}
              tabIndex={tabIndex}
               onSortChange={(value) => {
    setSortOrder(value);
fetchMessages(activeTab, 1, pageSize, value);
  }}
  sortOrder={sortOrder}
  onSelectionChange={handleSelectionChange}
            />

            <PaginationControl
              page={state[activeTab].page}
              totalItems={totalCount}
              pageSize={pageSize}
              onPageChange={(newPage) => fetchMessages(activeTab, newPage, pageSize)}
              sx={{ mt: 3, mb: 5 }}
            />

            <ReplyDrawer
              open={replyOpen}
              onClose={() => setReplyOpen(false)}
              selectedMessage={messageToReply}
            />
          </Container>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default InboxPage;